import React from "react";
import {
  LinkOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { colors, fontSize } from "~/styles/tokens";
import { JourneyWhyExpander } from "./JourneyWhyExpander";

const { Title, Paragraph } = Typography;

const FLOW = [
  {
    key: "shadow",
    icon: <UserOutlined />,
    labelKey: "JourneyGuide_People_Shadow_Label" as const,
    bodyKey: "JourneyGuide_People_Shadow_Body" as const,
    tone: "muted" as const,
  },
  {
    key: "invite",
    icon: <LinkOutlined />,
    labelKey: "JourneyGuide_People_Invite_Label" as const,
    bodyKey: "JourneyGuide_People_Invite_Body" as const,
    tone: "pulse" as const,
  },
  {
    key: "portal",
    icon: <UserSwitchOutlined />,
    labelKey: "JourneyGuide_People_Portal_Label" as const,
    bodyKey: "JourneyGuide_People_Portal_Body" as const,
    tone: "success" as const,
  },
];

interface PeopleJourneySectionProps {
  isMobile: boolean;
}

/**
 * People — shadow → invite → portal flow.
 */
export const PeopleJourneySection: React.FC<PeopleJourneySectionProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`journey-content${isMobile ? " is-mobile" : ""}`}>
      <Title level={isMobile ? 3 : 2} style={{ marginTop: 0 }}>
        {t("JourneyGuide_People_Title")}
      </Title>
      <Paragraph
        className="journey-lead"
        style={{
          fontSize: isMobile ? fontSize.body : fontSize.emphasis,
        }}
      >
        {t("JourneyGuide_People_Description")}
      </Paragraph>

      <ol className={`journey-flow${isMobile ? " is-stacked" : ""}`}>
        {FLOW.map((step) => (
          <li
            key={step.key}
            className={`journey-flow-step journey-flow-step--${step.tone}`}
          >
            <span
              className="journey-flow-icon"
              style={{
                background:
                  step.tone === "pulse"
                    ? colors.actionPrimary
                    : step.tone === "success"
                      ? colors.statusSuccess
                      : colors.borderDefault,
              }}
              aria-hidden
            >
              {step.icon}
            </span>
            <div className="journey-flow-copy">
              <div className="journey-flow-label">{t(step.labelKey)}</div>
              <p className="journey-flow-body">{t(step.bodyKey)}</p>
            </div>
          </li>
        ))}
      </ol>

      <JourneyWhyExpander
        summaryKey="JourneyGuide_People_Why_Summary"
        detailKey="JourneyGuide_People_Why_Detail"
      />
    </div>
  );
};
